import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import { useAuthStore } from '../stores/authStore'
import { useToast } from '../hooks/useToast'

interface StaffDashboardStats {
  // Personal Metrics
  assignedTickets: number
  resolvedTickets: number
  avgResolutionTime: number
  workloadScore: number
  
  // Team Metrics
  teamTickets: number
  teamPerformance: number
  teamWorkload: number
  
  // Recent Activity
  recentActivity: ActivityItem[]
  
  // Performance Trends
  performanceTrends: PerformanceData[]
  
  // Knowledge Base
  frequentArticles: Article[]
}

interface ActivityItem {
  id: string
  type: 'ticket_updated' | 'ticket_assigned' | 'comment_added' | 'status_changed'
  description: string
  timestamp: string
  ticketId?: string
  ticketTitle?: string
}

interface PerformanceData {
  date: string
  ticketsResolved: number
  avgResolutionTime: number
  customerSatisfaction: number
}

interface Article {
  id: string
  title: string
  category: string
  views: number
  lastViewed?: string
}

interface QuickAction {
  id: string
  label: string
  icon: string
  action: () => void
  color: string
}

const StaffDashboard: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const { showError, showAuthError } = useToast()
  
  const [stats, setStats] = useState<StaffDashboardStats>({
    assignedTickets: 0,
    resolvedTickets: 0,
    avgResolutionTime: 0,
    workloadScore: 0,
    teamTickets: 0,
    teamPerformance: 0,
    teamWorkload: 0,
    recentActivity: [],
    performanceTrends: [],
    frequentArticles: []
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchStaffDashboard = async () => {
      try {
        setIsLoading(true)
        setError(null)
        
        // Fetch staff dashboard data
        const response = await axios.get('/api/staff/dashboard')
        
        if (response.data.success) {
          setStats(response.data.data)
        } else {
          setError(response.data.error || 'Failed to fetch dashboard data')
        }
      } catch (error: any) {
        console.error('Error fetching staff dashboard:', error)
        
        // Handle authentication errors specifically
        if (error.response?.status === 401 || error.response?.status === 403) {
          showAuthError('Please log in again to access your dashboard')
          // Redirect to login after a short delay
          setTimeout(() => {
            navigate('/login')
          }, 2000)
        } else {
          setError(error.response?.data?.error || 'Failed to fetch dashboard data')
          showError('Unable to load dashboard data. Please try refreshing the page.')
        }
      } finally {
        setIsLoading(false)
      }
    }

    fetchStaffDashboard()
  }, [])

  const formatHours = (hours: number) => {
    if (hours === 0) return '0h'
    if (hours < 1) return '<1h'
    if (hours < 24) return `${Math.round(hours)}h`
    const days = Math.floor(hours / 24)
    const remainingHours = hours % 24
    if (remainingHours === 0) return `${days}d`
    return `${days}d ${Math.round(remainingHours)}h`
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'ticket_updated': return '📝'
      case 'ticket_assigned': return '🎯'
      case 'comment_added': return '💬'
      case 'status_changed': return '🔄'
      default: return '📋'
    }
  }

  const getWorkloadColor = (score: number) => {
    if (score < 30) return 'text-green-600'
    if (score < 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  const getWorkloadLabel = (score: number) => {
    if (score < 30) return 'Light'
    if (score < 70) return 'Moderate'
    return 'Heavy'
  }

  const quickActions: QuickAction[] = [
    {
      id: 'new-ticket',
      label: 'New Ticket',
      icon: '➕',
      action: () => navigate('/tickets/new'),
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      id: 'my-tickets',
      label: 'My Tickets',
      icon: '🎫',
      action: () => navigate('/tickets?assigned=me'),
      color: 'bg-green-500 hover:bg-green-600'
    },
    {
      id: 'knowledge-base',
      label: 'Knowledge Base',
      icon: '📚',
      action: () => navigate('/knowledge'),
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      id: 'team-calendar',
      label: 'Team Calendar',
      icon: '📅',
      action: () => navigate('/calendar'),
      color: 'bg-orange-500 hover:bg-orange-600'
    }
  ]

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium mb-4">
          Failed to load dashboard
        </div>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <h1 className="text-3xl font-semibold text-gray-900">Staff Dashboard</h1>
          <p className="mt-2 text-sm text-gray-700">
            Welcome back, {user?.firstName}! Here's your personalized work overview
          </p>
        </div>
      </div>
      
      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={action.action}
              className={`${action.color} text-white p-4 rounded-lg hover:shadow-md transition-all duration-200 flex flex-col items-center space-y-2`}
            >
              <span className="text-2xl">{action.icon}</span>
              <span className="text-sm font-medium">{action.label}</span>
            </button>
          ))}
        </div>
      </div>
      
      {/* Key Metrics */}
      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/tickets?assigned=me')}>
          <h3 className="text-lg font-medium text-gray-900">Assigned Tickets</h3>
          <p className="mt-2 text-3xl font-bold text-blue-600">{stats.assignedTickets}</p>
          <p className="mt-1 text-sm text-gray-500">Currently assigned to you</p>
        </div>
        
        <div className="card cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => navigate('/tickets?status=resolved&assigned=me')}>
          <h3 className="text-lg font-medium text-gray-900">Resolved This Month</h3>
          <p className="mt-2 text-3xl font-bold text-green-600">{stats.resolvedTickets}</p>
          <p className="mt-1 text-sm text-gray-500">Tickets you've resolved</p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Avg Resolution Time</h3>
          <p className="mt-2 text-3xl font-bold text-purple-600">{formatHours(stats.avgResolutionTime)}</p>
          <p className="mt-1 text-sm text-gray-500">Your average time</p>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900">Workload</h3>
          <p className={`mt-2 text-3xl font-bold ${getWorkloadColor(stats.workloadScore)}`}>
            {getWorkloadLabel(stats.workloadScore)}
          </p>
          <p className="mt-1 text-sm text-gray-500">Current workload level</p>
        </div>
      </div>

      {/* Team Metrics */}
      <div className="mt-8">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Team Overview</h2>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900">Team Tickets</h3>
            <p className="mt-2 text-3xl font-bold text-indigo-600">{stats.teamTickets}</p>
            <p className="mt-1 text-sm text-gray-500">Total team workload</p>
          </div>
          
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900">Team Performance</h3>
            <p className="mt-2 text-3xl font-bold text-emerald-600">{stats.teamPerformance}%</p>
            <p className="mt-1 text-sm text-gray-500">SLA compliance rate</p>
          </div>
          
          <div className="card">
            <h3 className="text-lg font-medium text-gray-900">Team Workload</h3>
            <p className={`mt-2 text-3xl font-bold ${getWorkloadColor(stats.teamWorkload)}`}>
              {getWorkloadLabel(stats.teamWorkload)}
            </p>
            <p className="mt-1 text-sm text-gray-500">Overall team capacity</p>
          </div>
        </div>
      </div>

      {/* Recent Activity & Knowledge Base */}
      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            {stats.recentActivity.length > 0 ? (
              stats.recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                  <span className="text-lg">{getActivityIcon(activity.type)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900">{activity.description}</p>
                    {activity.ticketTitle && (
                      <p className="text-xs text-gray-500 mt-1">
                        Ticket: {activity.ticketTitle}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      {formatDate(activity.timestamp)}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No recent activity</p>
            )}
          </div>
        </div>

        {/* Knowledge Base */}
        <div className="card">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Frequently Used Articles</h3>
          <div className="space-y-3">
            {stats.frequentArticles.length > 0 ? (
              stats.frequentArticles.map((article) => (
                <div key={article.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{article.title}</p>
                    <p className="text-xs text-gray-500">{article.category}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400">{article.views} views</span>
                    <button
                      onClick={() => navigate(`/knowledge/${article.id}`)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No frequent articles</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default StaffDashboard
